import { Network, BookOpen, FilePlus2, Upload } from 'lucide-react';
import { toast } from 'react-toastify';

import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb';
import Tabs from '../../../components/Tabs/Tabs';
import Table, { StatusPill } from '../../../components/Table/Table';

import usePayrollEntity from '../../../utils/usePayrollEntity';
import { fmtRs, fmtMoney } from '../../../utils/payrollFormat';
import { toIsoDate } from '../../../utils/dateFormat';

const ICON = { size: 15 };

const TABLE_PROPS = {
  search: true,
  searchLabel: 'Search',
  pageSize: 10,
  scrollClassName: '',
};

/* ── GL Mapping ──────────────────────────────────────────────────────────── */
const GLMappingTab = () => {
  const { list, loading } = usePayrollEntity('glMapping');

  return (
    <Table
      {...TABLE_PROPS}
      title="GL Mapping"
      loading={loading}
      rows={list}
      rowKey="id"
      searchKeys={['componentCode', 'componentName']}
      searchPlaceholder="Search GL mappings..."
      emptyMessage="No GL mappings found"
      columns={[
        { key: 'componentCode', label: 'Component', className: 'font-semibold text-gray-900' },
        { key: 'componentName', label: 'Component Name', grow: true, className: 'text-gray-700' },
        { key: 'debitAccount', label: 'Debit Account', className: 'text-gray-600 tabular-nums' },
        { key: 'creditAccount', label: 'Credit Account', className: 'text-gray-600 tabular-nums' },
        {
          key: 'status',
          label: 'Status',
          align: 'center',
          render: (r) => <StatusPill label={r.status} />,
        },
      ]}
    />
  );
};

/* ── Payroll Journal ─────────────────────────────────────────────────────── */
const PayrollJournalTab = () => {
  const { list, loading } = usePayrollEntity('payrollJournal');

  return (
    <Table
      {...TABLE_PROPS}
      title="Payroll Journal"
      loading={loading}
      rows={list}
      rowKey="id"
      searchKeys={['account']}
      searchPlaceholder="Search journal accounts..."
      emptyMessage="No journal accounts found"
      columns={[
        { key: 'account', label: 'Account', grow: true, className: 'font-semibold text-gray-900' },
        { key: 'period', label: 'Period', className: 'text-gray-600' },
        {
          key: 'debit',
          label: 'Debit (Rs)',
          align: 'right',
          className: 'text-gray-700',
          render: (r) => (r.debit ? fmtMoney(r.debit) : '-'),
        },
        {
          key: 'credit',
          label: 'Credit (Rs)',
          align: 'right',
          className: 'text-gray-700',
          render: (r) => (r.credit ? fmtMoney(r.credit) : '-'),
        },
      ]}
    />
  );
};

/* ── Journal Posting ─────────────────────────────────────────────────────── */
const JournalPostingTab = () => {
  const { list, loading, save } = usePayrollEntity('journalPosting');

  const postJournal = () =>
    save(
      {
        batch: 'July 2026 Monthly Batch',
        postedOn: '2026-08-01',
        postedBy: 'Finance Officer',
        status: 'Posted',
      },
      false
    ).then(() => toast.success('Payroll journal posted to Finance.', { autoClose: 2000 }));

  return (
    <Table
      {...TABLE_PROPS}
      title="Journal Posting"
      loading={loading}
      rows={list}
      rowKey="id"
      searchKeys={['batch', 'postedBy']}
      searchPlaceholder="Search journal postings..."
      emptyMessage="No journal postings found"
      onAdd={{ label: 'Post New Journal', onClick: postJournal }}
      columns={[
        { key: 'batch', label: 'Batch', grow: true, className: 'font-semibold text-gray-900' },
        {
          key: 'postedOn',
          label: 'Posted On',
          align: 'center',
          className: 'text-gray-600 tabular-nums',
          render: (r) => toIsoDate(r.postedOn),
        },
        { key: 'postedBy', label: 'Posted By', className: 'text-gray-600' },
        {
          key: 'status',
          label: 'Status',
          align: 'center',
          render: (r) => <StatusPill label={r.status} />,
        },
      ]}
    />
  );
};

/* ── Export to Finance ───────────────────────────────────────────────────── */
const ExportToFinanceTab = () => {
  const { list, loading } = usePayrollEntity('exportToFinance');
  const notify = (r) => toast.info(`Downloading export for ${r.batch} (demo).`, { autoClose: 2000 });

  return (
    <Table
      {...TABLE_PROPS}
      title="Export to Finance"
      loading={loading}
      rows={list}
      rowKey="id"
      searchKeys={['batch', 'format']}
      searchPlaceholder="Search finance exports..."
      emptyMessage="No finance exports found"
      columns={[
        { key: 'batch', label: 'Batch', grow: true, className: 'font-semibold text-gray-900' },
        { key: 'format', label: 'Format', className: 'text-gray-600' },
        {
          key: 'exportedOn',
          label: 'Exported On',
          align: 'center',
          className: 'text-gray-600 tabular-nums',
          render: (r) => toIsoDate(r.exportedOn),
        },
        {
          key: 'status',
          label: 'Status',
          align: 'center',
          render: (r) => <StatusPill label={r.status} />,
        },
        {
          key: 'actions',
          label: 'Actions',
          align: 'center',
          sortable: false,
          render: (r) => (
            <button
              type="button"
              onClick={() => notify(r)}
              className="inline-flex items-center gap-1.5 h-7 px-2 rounded-md text-[12px] font-semibold
                text-primary-700 hover:bg-primary-50 transition-colors"
            >
              <Upload size={14} />
              Download
            </button>
          ),
        },
      ]}
    />
  );
};

/* ── Page ────────────────────────────────────────────────────────────────── */
const FinanceIntegration = () => {
  const tabs = [
    { key: 'gl', label: 'GL Mapping', icon: <Network {...ICON} />, content: <GLMappingTab /> },
    { key: 'journal', label: 'Payroll Journal', icon: <BookOpen {...ICON} />, content: <PayrollJournalTab /> },
    { key: 'posting', label: 'Journal Posting', icon: <FilePlus2 {...ICON} />, content: <JournalPostingTab /> },
    { key: 'export', label: 'Export to Finance', icon: <Upload {...ICON} />, content: <ExportToFinanceTab /> },
  ];

  return (
    <div className="p-6 space-y-4">
      <Breadcrumb
        trail={['Payroll', 'Finance Integration']}
        title="Finance Integration"
        subtitle="Map payroll components to the general ledger and post payroll journals to eFinancials."
      />
      <Tabs tabs={tabs} defaultTab="gl" />
    </div>
  );
};

export default FinanceIntegration;